import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SCHEME_RETURN_CURRENCY } from '@/components/utils/schemeConstants';

/** The scheme-level fields the badges read. Any performance row shape can satisfy this. */
export interface SchemeAnnotationSource {
    scheme_code?: string;
    has_delayed_nav?: boolean;
}

interface SchemeBadge {
    key: string;
    color: string;
    /** Tooltip text for this scheme, or null when the badge does not apply to it. */
    tooltip: (scheme: SchemeAnnotationSource) => string | null;
}

// Declarative badge list — add an entry here to annotate schemes with something new.
const BADGES: SchemeBadge[] = [
    {
        key: 'currency',
        color: 'var(--info)',
        tooltip: scheme => {
            const currency = SCHEME_RETURN_CURRENCY[scheme.scheme_code ?? ''];
            return currency
                ? `Returns for this scheme are in ${currency}.`
                : null;
        },
    },
    {
        key: 'delayed-nav',
        color: 'var(--warn)',
        tooltip: scheme => (scheme.has_delayed_nav
            ? 'NAV data can sometimes be delayed by up to two business days (T-2) depending on reporting cycles.'
            : null),
    },
];

interface SchemePerfBadgesProps {
    scheme: SchemeAnnotationSource;
}

/**
 * Renders the info badges that apply to a scheme, beside its code in a performance table.
 * Expects to sit inside a flex container (e.g. `.scheme-cell-inner`).
 */
const SchemePerfBadges: React.FC<SchemePerfBadgesProps> = ({ scheme }) => (
    <>
        {BADGES.map(badge => {
            const title = badge.tooltip(scheme);
            if (!title) return null;
            return (
                <Tooltip key={badge.key} title={title} placement="right" arrow>
                    <InfoOutlinedIcon
                        style={{
                            color: badge.color,
                            cursor: 'pointer',
                            // Bumped slightly to 16px as MUI icons run a bit smaller than text
                            fontSize: '16px',
                            flexShrink: 0,
                        }}
                    />
                </Tooltip>
            );
        })}
    </>
);

export default SchemePerfBadges;
